import { Card } from '@/components/Elements'
import { Box, Flex, SimpleGrid } from '@chakra-ui/react'

import { DummyInput, Input, PriceInput, Select, Textarea } from '@/components/Form'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useContributorGroup } from '@/features/contributorGroups/useContributorGroup'
import { ContributorStatus, OfferPaymentCreditTerm, OfferStatus } from '@/features/offers/constants'
import { getTotalEuroLiability } from '@/features/offers/helpers'
import { getTotalContractPrice } from '@/features/offers/helpers/getTotalContractPrice'
import { isNullish } from '@/utils/helper'
import { numberWithCommas } from '@/utils/stringHelper'
import { useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

const OfferOverviewCommercialInfo = ({ isDisabled }) => {
  const { getValues, setValue } = useFormContext()
  const { append: addContributor } = useFieldArray({ name: 'contributors' })

  //Fetch
  const { useFetchAllContributorGroups } = useContributorGroup()

  const { data: contributorGroupsResponse } = useFetchAllContributorGroups({
    page: 1,
    pageSize: 0,
    filters: { status: 'active' },
  })

  const [isLiabilityCapDisabled, setIsLiabilityCapDisabled] = useState(false)
  //Watch
  const isSiemensStandardTerms = useWatch({ name: `commercialChecklist.isSiemensStandardTerms` })
  const employeeInsuranceProvider = useWatch({ name: 'commercialChecklist.employeeInsuranceProvider.value' })
  const contractorInsuranceProvider = useWatch({ name: 'commercialChecklist.contractorInsuranceProvider.value' })
  const totalLiabilityCap = useWatch({ name: `commercialChecklist.totalLiabilityCap.value` })
  const totalLiabilityCapPercentage = useWatch({ name: `commercialChecklist.totalLiabilityCap.other` })
  const subSystems = useWatch({ name: 'subSystems' })
  const euroExchangeRate = useWatch({ name: 'euroExchangeRate' })
  const currency = useWatch({ name: `currency` })
  //Translated
  const defaultLegalContributorGroup = contributorGroupsResponse?.data.find(
    (group) => group.type === ContributorGroupType.LEGAL
  )
  const defaultQSContributorGroup = contributorGroupsResponse?.data.find(
    (group) => group.type === ContributorGroupType.QS
  )

  const defaultLegalUser = !isNullish(defaultLegalContributorGroup) ? defaultLegalContributorGroup?.defaultUser : null
  const defaultQSUser = !isNullish(defaultQSContributorGroup) ? defaultQSContributorGroup?.defaultUser : null

  const siemensResponsibleInsurance =
    employeeInsuranceProvider === 'siemens' || contractorInsuranceProvider === 'siemens'
  const hasMaintenance = subSystems.filter((subsystem) => subsystem.type === 'maintenance').length > 0
  const hasProject = subSystems.filter((subsystem) => subsystem.type === 'project').length > 0
  const totalPrice = getTotalContractPrice(subSystems)
  const totalLiability = getTotalEuroLiability(
    totalLiabilityCap,
    totalLiabilityCapPercentage,
    totalPrice,
    euroExchangeRate
  )

  //Handler
  const handleIsSiemensStandardTermsChange = (val) => {
    const status = getValues('status')

    if (status !== OfferStatus.QUOTATION_DRAFT) {
      return
    }

    if (val === 'noWithLegalWaiver') {
      setValue('commercialChecklist.totalLiabilityCap.value', 'unlimited', { shouldDirty: true })
      setIsLiabilityCapDisabled(true)
    } else {
      setIsLiabilityCapDisabled(false)
    }

    if (val === 'no') {
      const contributors = getValues('contributors')
      if (!contributors.some((contributorDetail) => contributorDetail.group === ContributorGroupType.LEGAL)) {
        //Add Legal

        const newContributor = {
          group: ContributorGroupType.LEGAL,
          user: {
            _id: defaultLegalUser?._id || '',
            email: defaultLegalUser?.email || '',
          },
          dueDate: undefined,
          status: ContributorStatus.REQUIRED,
          hasEdit: false,
          canEdit: true,
        }
        addContributor(newContributor)
      }
      if (!contributors.some((contributorDetail) => contributorDetail.group === ContributorGroupType.QS)) {
        //Add Legal

        const newContributor = {
          group: ContributorGroupType.QS,
          user: {
            _id: defaultQSUser?._id || '',
            email: defaultQSUser?.email || '',
          },
          dueDate: undefined,
          status: ContributorStatus.REQUIRED,
          hasEdit: false,
          canEdit: true,
        }
        addContributor(newContributor)
      }
    }
  }

  return (
    <Card title={'Commercial Info'} hasBorder={false} canCollapse defaultIsOpen>
      <SimpleGrid columns={[1]} spacingX="40px" spacingY="15px" width={'50%'}>
        <Select
          name="commercialChecklist.isSiemensStandardTerms"
          label="Is Siemens Standard Term"
          disabled={isDisabled}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'noWithLegalWaiver', label: 'No (with legal waiver)' },
          ]}
          isRow
          extraOnChange={handleIsSiemensStandardTermsChange}
        />

        {isSiemensStandardTerms === 'no' || isSiemensStandardTerms === 'noWithLegalWaiver' ? (
          <>
            <Select
              name="commercialChecklist.hasPreviousAgreementBefore"
              label="Has Previous Agreement Before"
              isRow
              disabled={isDisabled}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
            />
            <DummyInput
              label="Siemens Responsible Insurance"
              value={siemensResponsibleInsurance ? 'yes' : 'no'}
              isRequired={false}
              isRow
            />
            <Select
              name="commercialChecklist.requireBond"
              label="Bond / Guarantee "
              isRow
              disabled={isDisabled}
              options={[
                { value: 'yes', label: 'yes' },
                { value: 'no', label: 'no' },
              ]}
            />
            <Flex gap="2" direction="column" alignItems={'end'}>
              <Select
                name="commercialChecklist.totalLiabilityCap.value"
                label="Total Liability Cap"
                isRow
                disabled={isDisabled || isLiabilityCapDisabled || isSiemensStandardTerms === 'noWithLegalWaiver'} //Liability Cap is disabled if isSiemensStandardTerms noWithLegalWaiver
                options={[
                  { value: 'limited', label: 'Total Liability capped at ($):' },
                  { value: 'limitedPercentage', label: 'Total Liability capped at (%):' },
                  { value: 'unlimited', label: 'Unlimited' },
                ]}
              />
              {totalLiabilityCap === 'limited' || totalLiabilityCap === 'limitedPercentage' ? (
                <Box w="69%">
                  <PriceInput
                    name="commercialChecklist.totalLiabilityCap.other"
                    isRow
                    disabled={isDisabled}
                    labelWidth="0"
                  />
                </Box>
              ) : (
                <Box></Box>
              )}
            </Flex>
          </>
        ) : (
          <Box></Box>
        )}

        <Select
          name="commercialChecklist.paymentCreditTerm.value"
          label="Credit Term"
          isRow
          disabled={isDisabled}
          options={Object.values(OfferPaymentCreditTerm).map((creditTerm) => {
            return {
              value: creditTerm,
              label: creditTerm,
            }
          })}
        />

        {hasMaintenance && (
          <Box px="3">
            <Textarea
              name="commercialChecklist.paymentBillingMilestoneMaintenance"
              label="Billing Milestone (Maintenance)"
              placeholder=""
              disabled={isDisabled}
              labelWidth={50}
              rows="5"
              isRequired={false}
            />
          </Box>
        )}

        {hasProject && (
          <Box px="3">
            <Textarea
              name="commercialChecklist.paymentBillingMilestoneProject"
              label="Billing Milestone (Project)"
              placeholder=""
              disabled={isDisabled}
              isRequired={false}
              labelWidth={50}
              rows="5"
            />
          </Box>
        )}

        {totalPrice >= 120000000 && (
          <>
            <Input
              name="euroExchangeRate"
              label="Euro Exchange Rate (Euro : HKD)"
              type="number"
              isRow
              min="0"
              placeholder="Enter value"
              disabled={isDisabled}
            />
            <DummyInput label="Total Liability (€)" value={numberWithCommas(totalLiability)} isRow />
          </>
        )}
      </SimpleGrid>
    </Card>
  )
}

export default OfferOverviewCommercialInfo
